@let deletedUserValue = deletedUser();

@if (deletedUserValue) {
  <div class="p-6">
    <div class="rounded-lg border border-red-300 bg-red-50 p-6 max-w-2xl">
      <div class="flex items-center gap-3 mb-4">
        <i class="pi pi-trash text-red-600 text-2xl"></i>
        <h1 class="font-bold text-2xl text-red-800">User deleted</h1>
        <p-tag severity="danger">DELETED</p-tag>
      </div>
      <p class="text-sm text-red-700 mb-6">
        This user has been deleted. Only minimal tombstone information is retained.
      </p>
      <div class="grid grid-cols-1 gap-y-4">
        <div>
          <span class="text-sm text-muted-color">User ID</span>
          <div class="flex items-center gap-2">
            <span class="font-mono text-sm break-all">{{ deletedUserValue.id }}</span>
            <homey-copy-button [value]="deletedUserValue.id"/>
          </div>
        </div>
        <div>
          <span class="text-sm text-muted-color">Email at deletion</span>
          <div class="flex items-center gap-2">
            <span class="text-sm break-all">{{ deletedUserValue.email ?? 'Unknown' }}</span>
            @if (deletedUserValue.email) {
              <homey-copy-button [value]="deletedUserValue.email"/>
            }
          </div>
        </div>
        <div>
          <span class="text-sm text-muted-color">Deleted at</span>
          <div class="text-sm">
            {{ deletedUserValue.deletedAt ? (deletedUserValue.deletedAt | date: 'dd-MM-yyyy HH:mm:ss') : 'Unknown' }}
          </div>
        </div>
      </div>
    </div>
  </div>
}

@let userValue = activeUser();

@if (userValue) {
  @let syncTag = syncProcessingTag();
  <div class="p-6">
    <h1 class="font-bold text-3xl">
      {{ userValue.id }}
      <p-tag [severity]="homeyStatusToTag(userValue.status)">{{ userValue.status }}</p-tag>
      @if (userValue.ignore) {
        <p-tag severity="danger">IGNORED</p-tag>
        <p-tag [severity]="ignoredReasonSeverity(userValue.ignoredReason)">
          {{ ignoredReasonLabel(userValue.ignoredReason) }}
        </p-tag>
      }
      @if (syncTag) {
        <p-tag [severity]="syncTag.severity">{{ syncTag.label }}</p-tag>
      }
    </h1>

    <div class="flex flex-row justify-between">
      <div class="max-w-2xl">
        <p class="text-sm text-muted-color mb-6">Created at {{ userValue.createdAt | date: 'dd-MM-yyyy HH:mm:ss' }}</p>

        <div class="grid grid-cols-2 gap-y-6">
          <homey-labeled label="First name" [value]="userValue.firstName"/>
          <homey-labeled label="Last name" [value]="userValue.lastName"/>

          <homey-labeled label="Subscription status" [value]="userValue.subscription?.status"
                          [tagFunction]="subscriptionStatusToTag"/>
          <homey-labeled label="User app access" [value]="userValue.subscription?.paymentStatus"
                          [tagFunction]="paymentStatusToTag"/>

          <homey-labeled label="Last seen" [value]="userValue.lastSeen | date: 'dd-MM-yyyy HH:mm:ss'"/>
          <homey-labeled label="Trialing" [value]="userValue.trialing ? 'Yes' : 'No'"/>

          <homey-labeled label="Email" [value]="userValue.email"/>
          <homey-labeled label="Phone" [value]="userValue.phone"/>

          <homey-labeled label="Household size" [value]="'' + (userValue.householdInfo?.members?.length ?? '')"/>
          <homey-labeled label="Rental assistance" [value]="userValue.rentalAssistance ? 'Yes' : 'No'"/>
          <homey-labeled label="Birth date" [value]="userValue.birthDate"/>

          <homey-labeled label="Subscription type" [value]="userValue.subscription?.type"/>
          <homey-labeled label="Next payment date" [value]="userValue.subscription?.nextPaymentDate | date: 'dd-MM-yyyy'"/>
          <homey-labeled label="Referring source" [value]="userValue.source"/>

          <homey-labeled label="HC Profile Status" [value]="userValue.housingConnectProfile?.status"
                          [tagFunction]="nycProfileStatusToTag"/>
          <homey-labeled label="HC Last Sync Status" [value]="userValue.housingConnectProfile?.lastSyncStatus"
                          [tagFunction]="lastSyncStatusToTag"/>
          <homey-labeled label="HC Login Status" [value]="userValue.housingConnectProfile?.loginStatus"
                          [tagFunction]="loginStatusToTag"/>
          <homey-labeled label="HC Login Failed Count"
                          [value]="'' + (userValue.housingConnectProfile?.loginFailedCount ?? 0)"
                          [tagFunction]="failureCountToTag"/>
          <homey-labeled label="HC Sync Failed Count"
                          [value]="'' + (userValue.housingConnectProfile?.syncFailedCount ?? 0)"
                          [tagFunction]="failureCountToTag"/>
          <homey-labeled label="HC Last synced at"
                          [value]="userValue.housingConnectProfile?.lastSyncedAt | date: 'dd-MM-yyyy HH:mm:ss'"/>
          <homey-labeled label="HC Last sync considered at"
                          [value]="userValue.housingConnectProfile?.lastSyncConsideredAt | date: 'dd-MM-yyyy HH:mm:ss'"/>
          <homey-labeled label="HC Last sync error" [value]="userValue.housingConnectProfile?.lastSyncError"/>
          <homey-labeled label="HC Ignored" [value]="userValue.housingConnectProfile?.ignore ? 'Yes' : 'No'"/>
          <homey-labeled label="HC Ignored reason" [value]="userValue.housingConnectProfile?.ignoredReason"/>
          <homey-labeled label="HC Suppress unknown emails"
                          [value]="userValue.housingConnectProfile?.suppressUnknownEmails ? 'Yes' : 'No'"/>

          <homey-labeled label="HC Email 1" [value]="userValue.housingConnectProfile?.email1"/>
          <homey-labeled label="HC Email 2" [value]="userValue.housingConnectProfile?.email2"/>
          <homey-labeled label="HC Username" [value]="userValue.housingConnectProfile?.username"/>
          <homey-labeled label="HC Password" [value]="userValue.housingConnectProfile?.password" [copy]="true"/>
        </div>
      </div>

      <div class="flex flex-col gap-2 mt-12 sticky top-6 self-start max-h-[calc(100vh-4rem)] overflow-y-auto">
        <homey-submit-button
          label="Register in NYC"
          [submitFunction]="registerInNyc"
          [disabled]="registerInNycDisabled()"
        />
        <homey-submit-button
          label="Unstuck"
          [submitFunction]="unstuck"
          [disabled]="unstuckDisabled()"
        />
        <p-button label="Send text message" icon="pi pi-comment" (onClick)="goToSendTextMessage()"/>
        <p-button label="Delete" severity="danger" icon="pi pi-trash" (onClick)="confirmDelete()"/>
      </div>
    </div>

    <div class="mt-8">
      <h2 class="text-xl font-medium mb-3">Connected accounts</h2>
      <div class="border surface-card rounded-md p-4">
        @if (userValue.connectedAccounts.length) {
          <div class="flex flex-col gap-3">
            @for (account of userValue.connectedAccounts; track account.authId) {
              <div class="flex flex-wrap items-center justify-between gap-4">
                <div class="flex flex-col gap-1">
                  <span class="font-mono text-sm text-surface-700">{{ account.authId }}</span>
                  <span class="text-sm text-muted-color">{{ account.username ?? 'No username stored' }}</span>
                </div>
                <p-tag [severity]="connectedAccountSeverity(account.provider)">
                  {{ connectedAccountProviderLabel(account.provider) }}
                </p-tag>
              </div>
            }
          </div>
        } @else {
          <p class="text-sm text-muted-color">No connected accounts recorded.</p>
        }
      </div>
    </div>

    <h2 class="mt-12 text-xl font-medium">Emails</h2>
    <p-table [value]="userValue.mails" [scrollable]="true" [scrollHeight]="tableScrollHeight">
      <ng-template pTemplate="header">
        <tr>
          <th>Id</th>
          <th>Status</th>
          <th>Recipient</th>
          <th>Subject</th>
          <th>Timestamp</th>
          <th>Error message</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-email [homeyTypedTableRow]="userValue.mails">
        <tr>
          <td>{{ email.id }}</td>
          <td>
            <p-tag>{{ email.status }}</p-tag>
          </td>
          <td>{{ email.recipient }}</td>
          <td>{{ email.subject }}</td>
          <td>{{ email.timestamp | date: 'dd-MM-yyyy HH:mm:ss' }}</td>
          <td>
            @if (email.status !== 'processed') {
              {{ email.errorMessage }}
            }
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6" class="text-center">No emails found</td>
        </tr>
      </ng-template>
    </p-table>

    <h2 class="mt-12 text-xl font-medium">Applications</h2>
    <p-table [value]="userValue.applications" [scrollable]="true" [scrollHeight]="tableScrollHeight">
      <ng-template pTemplate="header">
        <tr>
          <th>Apartment Id</th>
          <th>Status</th>
          <th>Method</th>
          <th>Created At</th>
          <th>Log Nr</th>
          <th>NYC Status</th>
          <th>Withdrawn</th>
          <th>Failure Reason</th>
          <th>Id</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-application [homeyTypedTableRow]="userValue.applications">
        <tr>
          <td>{{ application.lotteryId }}</td>
          <td>
            <p-tag [severity]="applicationStatusToTag(application.status)">{{ application.status }}</p-tag>
          </td>
          <td>{{ application.method }}</td>
          <td>{{ application.createdAt | date: 'dd-MM-yyyy HH:mm:ss' }}</td>
          <td>{{ application.logNr }}</td>
          <td>{{ application.nycStatusLabel }}</td>
          <td>{{ application.withdrawnManually }}</td>
          <td>{{ application.failureReason }}</td>
          <td>{{ application.id }}</td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="9" class="text-center">No applications found</td>
        </tr>
      </ng-template>
    </p-table>

    <h2 class="mt-12 text-xl font-medium">Notification channels</h2>
    <div class="grid gap-4 mt-4 md:grid-cols-3">
      <div class="p-4 border surface-card rounded-md">
        <div class="flex justify-between items-center">
          <span class="font-semibold">Email channel</span>
          <p-tag [severity]="userValue.notificationChannels.email.enabled ? 'success' : 'danger'">
            {{ userValue.notificationChannels.email.enabled ? 'Enabled' : 'Disabled' }}
          </p-tag>
        </div>
        <p class="text-sm mt-2 text-muted-color">
          Address: {{ userValue.notificationChannels.email.emailAddress || 'Not configured' }}
        </p>
      </div>
      <div class="p-4 border surface-card rounded-md">
        <div class="flex justify-between items-center">
          <span class="font-semibold">SMS channel</span>
          <p-tag
            [severity]="userValue.notificationChannels.text.enabled && !userValue.notificationChannels.text.optedOut ? 'success' : 'danger'">
            {{ userValue.notificationChannels.text.enabled && !userValue.notificationChannels.text.optedOut ? 'Enabled' : 'Disabled' }}
          </p-tag>
        </div>
        <p class="text-sm mt-2 text-muted-color">
          Phone: {{ userValue.notificationChannels.text.phoneNr || 'Not configured' }}
        </p>
        <p class="text-sm text-muted-color">
          Opted out: {{ userValue.notificationChannels.text.optedOut ? 'Yes' : 'No' }}
        </p>
      </div>
      <div class="p-4 border surface-card rounded-md">
        <div class="flex justify-between items-center">
          <span class="font-semibold">Push channel</span>
          <p-tag [severity]="userValue.notificationChannels.push.enabled ? 'success' : 'danger'">
            {{ userValue.notificationChannels.push.enabled ? 'Enabled' : 'Disabled' }}
          </p-tag>
        </div>
        <p class="text-sm mt-2 text-muted-color">
          Tokens: {{ userValue.notificationChannels.push.tokens.length }}
        </p>
      </div>
    </div>

    @if (userValue.notificationCategoryPreferences; as preferences) {
      <h2 class="mt-12 text-xl font-medium">Notification category preferences</h2>
      <div class="grid gap-4 mt-4 md:grid-cols-4">
        <div class="p-4 border surface-card rounded-md">
          <div class="flex justify-between items-center">
            <span class="font-semibold">Core</span>
            <p-tag [severity]="preferences.core ? 'success' : 'danger'">
              {{ preferences.core ? 'Enabled' : 'Disabled' }}
            </p-tag>
          </div>
        </div>
        <div class="p-4 border surface-card rounded-md">
          <div class="flex justify-between items-center">
            <span class="font-semibold">Promotional</span>
            <p-tag [severity]="preferences.promotional ? 'success' : 'danger'">
              {{ preferences.promotional ? 'Enabled' : 'Disabled' }}
            </p-tag>
          </div>
        </div>
        <div class="p-4 border surface-card rounded-md">
          <div class="flex justify-between items-center">
            <span class="font-semibold">Lottery</span>
            <p-tag [severity]="preferences.lottery ? 'success' : 'danger'">
              {{ preferences.lottery ? 'Enabled' : 'Disabled' }}
            </p-tag>
          </div>
        </div>
        <div class="p-4 border surface-card rounded-md">
          <div class="flex justify-between items-center">
            <span class="font-semibold">Lottery follow-up</span>
            <p-tag [severity]="preferences.lotteryfollowup ? 'success' : 'danger'">
              {{ preferences.lotteryfollowup ? 'Enabled' : 'Disabled' }}
            </p-tag>
          </div>
        </div>
      </div>
    }

    <h2 class="mt-12 text-xl font-medium">Notifications</h2>
    <p-table [value]="userValue.notifications" [scrollable]="true" [scrollHeight]="tableScrollHeight">
      <ng-template pTemplate="header">
        <tr>
          <th>Type</th>
          <th>Created at</th>
          <th>Read status</th>
          <th>Send statuses</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-notification [homeyTypedTableRow]="userValue.notifications">
        <tr>
          <td>{{ notification.type }}</td>
          <td>{{ notification.createdAt | date: 'dd-MM-yyyy HH:mm:ss' }}</td>
          <td>{{ notification.readStatus }}</td>
          <td>
            <div class="flex flex-col gap-1">
              @for (status of notification.sendStatuses; track status) {
                <div class="text-sm">
                  {{ status.channel }}: {{ status.sendStatus }}
                  @if (status.failureReason) {
                    <span class="text-muted-color">(failure: {{ status.failureReason }})</span>
                  }
                  @if (status.notNeededReason) {
                    <span class="text-muted-color">(not needed: {{ status.notNeededReason }})</span>
                  }
                </div>
              }
              @if (!notification.sendStatuses.length) {
                <span class="text-muted-color">-</span>
              }
            </div>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="4" class="text-center">No notifications found</td>
        </tr>
      </ng-template>
    </p-table>

    <h2 class="mt-12 text-xl font-medium">In-app notifications</h2>
    <p-table [value]="userValue.inAppNotifications" [scrollable]="true" [scrollHeight]="tableScrollHeight">
      <ng-template pTemplate="header">
        <tr>
          <th>Type</th>
          <th>Status</th>
          <th>Created at</th>
          <th>Read at</th>
          <th>Data</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-notification [homeyTypedTableRow]="userValue.inAppNotifications">
        <tr>
          <td>{{ notification.type }}</td>
          <td>
            <p-tag [severity]="notification.status === 'read' ? 'success' : 'warn'">{{ notification.status }}</p-tag>
          </td>
          <td>{{ notification.createdAt | date: 'dd-MM-yyyy HH:mm:ss' }}</td>
          <td>{{ notification.readAt | date: 'dd-MM-yyyy HH:mm:ss' }}</td>
          <td>
            <pre class="text-xs whitespace-pre-wrap">{{ notification.data | json }}</pre>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="5" class="text-center">No in-app notifications found</td>
        </tr>
      </ng-template>
    </p-table>

    <h2 class="mt-12 text-xl font-medium">Domain events</h2>
    <p-table [value]="userValue.domainEvents" [scrollable]="true" [scrollHeight]="tableScrollHeight">
      <ng-template pTemplate="header">
        <tr>
          <th>ID</th>
          <th>Type</th>
          <th>Timestamp</th>
          <th>Payload</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-event [homeyTypedTableRow]="userValue.domainEvents">
        <tr>
          <td>{{ event.id }}</td>
          <td>{{ event.eventType }}</td>
          <td>{{ event.timestamp | date: 'dd-MM-yyyy HH:mm:ss' }}</td>
          <td>
            <pre class="text-xs whitespace-pre-wrap">{{ event.payload | json }}</pre>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="4" class="text-center">No domain events found</td>
        </tr>
      </ng-template>
    </p-table>
  </div>

  @if (userValue.householdInfo; as householdInfo) {
    <div class="p-8">
      <h2 class="text-2xl font-medium mb-4">Household</h2>
      <div class="flex flex-row gap-8 mb-6">
        <homey-labeled label="Sync status" [value]="householdInfo.nycData.syncStatus"
                        [tagFunction]="syncStatusToTag"/>
      </div>

      @for (member of householdInfo.members; track member.id) {
        <p class="text-xl mb-4 font-medium mt-10">{{ member.firstName }} {{ member.lastName }}</p>
        <div class="flex flex-row gap-8 mb-2">
          <homey-labeled label="Sync status" [value]="member.nycData.syncStatus"
                          [tagFunction]="syncStatusToTag"/>
        </div>

        <p class="text-lg mb-2 font-medium mt-6">Income</p>
        <div class="flex flex-row gap-8 mb-2">
          <homey-labeled label="Sync status" [value]="member.income.nycData.syncStatus"
                          [tagFunction]="syncStatusToTag"/>
        </div>
        <p-table [value]="member.income.properties" class="mb-8">
          <ng-template pTemplate="header">
            <tr>
              <th>Type</th>
              <th>Amount</th>
              <th>Sync status</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-income [homeyTypedTableRow]="member.income.properties">
            <tr>
              <td>{{ income.type }}</td>
              <td>{{ income.value }}</td>
              <td>{{ income.nycData.syncStatus }}</td>
            </tr>
          </ng-template>
        </p-table>

        <p class="mb-2 text-lg font-medium mt-6">Assets</p>
        <div class="flex flex-row gap-8 mb-2">
          <homey-labeled label="Sync status" [value]="member.assets.nycData.syncStatus"
                          [tagFunction]="syncStatusToTag"/>
        </div>
        <p-table [value]="member.assets.properties" class="mb-8">
          <ng-template pTemplate="header">
            <tr>
              <th>Type</th>
              <th>Value</th>
              <th>Sync status</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-asset [homeyTypedTableRow]="member.assets.properties">
            <tr>
              <td>{{ asset.type }}</td>
              <td>{{ asset.value }}</td>
              <td>{{ asset.nycData.syncStatus }}</td>
            </tr>
          </ng-template>
        </p-table>
      }
    </div>
  }
}

<p-dialog
  header="Register in NYC"
  [modal]="true"
  [visible]="nycAuth() !== undefined"
  (visibleChange)="closeRegisterInNycDialog()"
  [style]="{ width: '50rem' }">
  @if (nycAuth(); as nycAuthValue) {
    <div class="flex flex-row justify-between items-center mb-6">
      <span class="p-text-secondary block">NYC Profile Information</span>
    </div>
    <div class="flex items-center gap-4 mb-4">
      <div class="font-semibold w-24">Email 1</div>
      <div>{{ nycAuthValue.email1 }}</div>
    </div>
    <div class="flex items-center gap-4 mb-4">
      <div class="font-semibold w-24">Email 2</div>
      <div>{{ nycAuthValue.email2 }}</div>
    </div>
    <div class="flex items-center gap-4 mb-4">
      <div class="font-semibold w-24">Username</div>
      <div>{{ nycAuthValue.username }}</div>
    </div>
    <div class="flex items-center gap-4 mb-8">
      <div class="font-semibold w-24">Password</div>
      <div>{{ nycAuthValue.password }}</div>
    </div>
    <div class="flex justify-end gap-2">
      <p-button label="Cancel" severity="secondary" (onClick)="closeRegisterInNycDialog()"/>
      <homey-submit-button label="Mark as registered" [submitFunction]="confirmRegisteredInNyc"/>
    </div>
  }
</p-dialog>
